import { useState, useEffect, useCallback, useMemo } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../atoms/Card/Card"
import Button from "../../atoms/Button/Button"
import Popup from "../../atoms/Popup/Popup"
import Select from "../../atoms/Select/Select"
import { HealthProfileStep } from "./components/diet-builder/HealthProfileStep"
import { PreferencesStep } from "./components/diet-builder/PreferencesStep"
import AIAssessmentHandler from "./components/AIAssessmentHandler"
import useAIAssessmentWrapper from "../AssessmentAISummary/hooks/useAIAssessmentWrapper"
import useDietPlanData from "./hooks/useDietPlanData"
import { generateAIDietPlan } from "../../services/rest/zyvelorAPI"
import "./AIGeneratedDietPlan.scss"
import { useParams } from "react-router-dom"
import { 
  DietFormData,
  ComponentProps 
} from "./types"
import {
  getDietPlanOptions,
  getSelectedDietPlanStatus,
  initializeFormData,
  transformUnifiedFormDataToAPIFormat,
  transformAIDietPlanToAddDietPlanFormat
} from "./utils"
import { constants } from "../../constants/common"

export default function AIGeneratedDietPlan({ onAutofill, onCloseToIndex }: ComponentProps) {
  const params = useParams()
  const patientInfo = JSON.parse(localStorage.getItem("PatientInfo") || "{}")
  const patientId = params?.patientId || patientInfo?.patient_id

  const {
    data: assessmentData,
    isLoading: isLoadingAssessmentData,
  } = useAIAssessmentWrapper()

  const {
    data: dietPlanData,
    isLoading: isLoadingDietPlanData,
    fetchDietPlanData,
  } = useDietPlanData()

  // Core state
  const [selectedAssessment, setSelectedAssessment] = useState<string>("")
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<string>("")
  
  // UI state
  const [isLoadingAssessment, setIsLoadingAssessment] = useState(false)
  const [showGeneratePlanPopup, setShowGeneratePlanPopup] = useState(false)
  const [assessmentLoaded, setAssessmentLoaded] = useState(false)
  const [noDataAvailable, setNoDataAvailable] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [hasFetchedData, setHasFetchedData] = useState(false)

  // Form state
  const [step1Completed, setStep1Completed] = useState(false)
  const [formData, setFormData] = useState<DietFormData>(initializeFormData())
  const [formDataModified, setFormDataModified] = useState(false)

  // Computed values
  const dietPlanOptions = useMemo(() => getDietPlanOptions(dietPlanData), [dietPlanData])
  const selectedDietPlanStatus = useMemo(
    () => getSelectedDietPlanStatus(selectedDropdownOption, dietPlanData),
    [selectedDropdownOption, dietPlanData]
  )
  
  // Check if selected assessment is in progress (not Success)
  const isAssessmentInProgress = useMemo(() => {
    return Boolean(selectedDropdownOption && selectedDietPlanStatus && selectedDietPlanStatus !== constants.SUCCESS)
  }, [selectedDropdownOption, selectedDietPlanStatus])

  // Automatically select the last assessment when data is loaded and call API
  useEffect(() => {
    if (assessmentData?.length > 0 && !hasFetchedData) {
      const lastAssessment = assessmentData[assessmentData.length - 1]
      setSelectedAssessment(lastAssessment.value)
      fetchDietPlanData(lastAssessment.value)
      setHasFetchedData(true)
    }
  }, [assessmentData, hasFetchedData, fetchDietPlanData])

  // Reset fetch flag when assessment changes
  useEffect(() => {
    if (selectedAssessment && dietPlanData?.assessment_id !== parseInt(selectedAssessment)) {
      setHasFetchedData(false)
    }
  }, [selectedAssessment, dietPlanData?.assessment_id])

  // Initialize form data when diet plan data changes
  useEffect(() => {
    if (dietPlanData && !formDataModified) {
      const initializedData = initializeFormData(dietPlanData, selectedDropdownOption)
      setFormData(initializedData)
    }
  }, [dietPlanData, formDataModified, selectedDropdownOption])

  // Update form data when dropdown selection changes
  useEffect(() => {
    if (dietPlanData && selectedDropdownOption) {
      const initializedData = initializeFormData(dietPlanData, selectedDropdownOption)
      setFormData(initializedData)
      setFormDataModified(false)
    }
  }, [selectedDropdownOption, dietPlanData])

  const fetchAssessmentData = useCallback(async (assessmentId: string) => {
    setIsLoadingAssessment(true)
    setNoDataAvailable(false)
    setAssessmentLoaded(false)

    try {
      if (!dietPlanData) {
        setNoDataAvailable(true)
        setIsLoadingAssessment(false)
        return
      }

      // Form data will be initialized by the useEffect above
      setStep1Completed(true)
      setAssessmentLoaded(true)
    } catch (error) {
      console.error("[v0] Error fetching assessment data:", error)
      setNoDataAvailable(true)
    } finally {
      setIsLoadingAssessment(false)
    }
  }, [dietPlanData])

  const handleLoadAssessment = useCallback(() => {
    if (!selectedAssessment || !dietPlanData) return
    
    // setSelectedDropdownOption("default")
    setIsGenerating(true)
    setHasFetchedData(false) // Reset flag to allow fresh data fetch
    fetchAssessmentData(selectedAssessment)
  }, [selectedAssessment, dietPlanData, fetchAssessmentData])
  
  const handleStep1Complete = useCallback((data: Partial<DietFormData>) => {
    setFormData(prev => ({ ...prev, ...data }))
    setStep1Completed(true)
  }, [])

  const handleFormDataUpdate = useCallback((data: Partial<DietFormData>) => {
    setFormData(prev => ({ ...prev, ...data }))
    setFormDataModified(true)
  }, [])

  const handleEditStep1 = useCallback(() => {
    setStep1Completed(false)
  }, [])

  const handleGeneratePlan = useCallback(async () => {
    if (!formData) {
      console.error("Missing form data for plan generation")
      return
    }

    try {
      const patientProfile = transformUnifiedFormDataToAPIFormat(
        formData,
        patientId,
        selectedAssessment
      )
      
      await generateAIDietPlan(patientProfile)

      if (selectedAssessment) {
        await fetchDietPlanData(selectedAssessment)
      }
      
      alert("Diet plan generation started successfully! Check back later for results.")
    } catch (error) {
      console.error("Error generating diet plan:", error)
      alert("Error generating diet plan. Please try again.")
    }
  }, [formData, patientId, selectedAssessment, fetchDietPlanData])

  const handleStep2Complete = useCallback((data: Partial<DietFormData>) => {
    setFormData(prev => ({ ...prev, ...data }))
    handleGeneratePlan()
    setShowGeneratePlanPopup(true)
  }, [handleGeneratePlan])

  const handleAutofill = useCallback(() => {
    if (!selectedDropdownOption || selectedDietPlanStatus !== constants.SUCCESS) return

    let selectedPlanData = null

    if (selectedDropdownOption === constants.DEFAULT) {
      if (dietPlanData?.diet_plan) {
        selectedPlanData = {
          diet_plan: "Default",
          patient_profile_v0: dietPlanData.diet_plan.patient_profile_v0,
          personalized_diet_plan: dietPlanData.diet_plan.personalized_diet_plan
        }
      } else if (dietPlanData?.top_5_regenerated_diet_plans?.length > 0) {
        selectedPlanData = dietPlanData.top_5_regenerated_diet_plans[0]
      }
    } else {
      selectedPlanData = dietPlanData?.top_5_regenerated_diet_plans?.find(
        (plan: any) => plan.generation_id === selectedDropdownOption
      )
    }

    if (selectedPlanData) {
      const transformedData = transformAIDietPlanToAddDietPlanFormat(selectedPlanData)
      if (transformedData && onAutofill) {
        onAutofill(transformedData)
      }
    }
  }, [selectedDropdownOption, selectedDietPlanStatus, dietPlanData, onAutofill])

  const handleRefresh = useCallback(() => {
    if (selectedAssessment) {
      setHasFetchedData(false) // Reset flag to allow fresh data fetch
      fetchDietPlanData(selectedAssessment)
    }
  }, [selectedAssessment, fetchDietPlanData])

  const getAssessmentLabel = useCallback(() => {
    if (assessmentData?.length > 0) {
      const lastAssessment = assessmentData[assessmentData.length - 1]
      return lastAssessment.key.replace("-", " ")
    }
    return ""
  }, [assessmentData])

  return (
    <div className="ai-diet-plan-container">
      <h1 className="page-title">
        AI Diet Plan Builder
      </h1>
      
      <AIAssessmentHandler 
        isLoading={isLoadingAssessmentData}
        assessmentData={assessmentData}
      >
        <>
          <Card className="assessment-card">
            <CardHeader>
              <div className="assessment-header">
                <div className="header-content">
                  <CardTitle className="header-title">Assessment Selection</CardTitle>
                  <CardDescription className="header-description">Select an assessment number to pre-fill data</CardDescription>
                </div>
                <div className="header-actions">
                  <Button
                    onClick={handleLoadAssessment}
                    disabled={isLoadingAssessment || isLoadingAssessmentData || isAssessmentInProgress}
                    loading={isLoadingAssessment}
                  >
                    {isLoadingAssessment ? "Loading..." : "Generate"}
                  </Button>
                  <div className="action-buttons">
                    <Button
                      onClick={handleRefresh}
                      disabled={
                        isLoadingAssessment || 
                        isLoadingAssessmentData || 
                        !selectedDropdownOption ||
                        selectedDietPlanStatus === constants.SUCCESS
                      }
                    >
                      Refresh
                    </Button> 
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="assessment-content">
                <div className="assessment-info">
                  <span className="assessment-label">
                    {getAssessmentLabel()}
                  </span>
                </div>

                <div className="diet-plan-selection">
                  <div className="compact-select">
                    <Select
                      value={selectedDropdownOption}
                      onChange={setSelectedDropdownOption}
                      placeholder={isLoadingDietPlanData ? "Loading diet plans..." : `Select a diet plan (${dietPlanOptions.length} available)`}
                      options={dietPlanOptions}
                      disabled={isLoadingDietPlanData || isGenerating}
                    />
                  </div>

                  <div className="autofill-section">
                    <Button
                      onClick={handleAutofill}
                      disabled={
                        isLoadingAssessment || 
                        isLoadingAssessmentData || 
                        !selectedDropdownOption || 
                        selectedDietPlanStatus !== constants.SUCCESS ||
                        isGenerating ||
                        isAssessmentInProgress
                      }
                    >
                      Auto Fill
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {noDataAvailable && (
            <Card className="no-data-card">
              <CardContent className="no-data-content">
                <div className="warning-icon">⚠️</div>
                <CardTitle className="no-data-title">
                  No Data Available
                </CardTitle>
                <p className="no-data-description">
                  No diet plan data is available for Assessment {selectedAssessment}. Please try a different assessment number.
                </p>
              </CardContent>
            </Card>
          )}

          <div className="steps-container">
            {assessmentLoaded && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Health Profile & Meal Slots</CardTitle>
                    <CardDescription>Demographics, goals, and meal preferences</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <HealthProfileStep
                      initialData={formData}
                      onComplete={handleStep1Complete}
                      onDataUpdate={handleFormDataUpdate}
                      isCompleted={step1Completed}
                      onEdit={handleEditStep1}
                      apiData={dietPlanData}
                    />
                  </CardContent>
                </Card>

                {step1Completed && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Preferences & Medical Context</CardTitle>
                      <CardDescription>Dietary preferences and medical information</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <PreferencesStep
                        initialData={formData}
                        onComplete={handleStep2Complete}
                        onBack={handleEditStep1}
                        onDataUpdate={handleFormDataUpdate}
                        apiData={dietPlanData}
                        selectedDropdownOption={selectedDropdownOption}
                      />
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </>
      </AIAssessmentHandler>

      <Popup 
        show={showGeneratePlanPopup}
        headerText="Generate Diet Plan"
        onClose={() => setShowGeneratePlanPopup(false)}
      >
        <div className="popup-content">
          <div>
            <p className="popup-message">
              We will take a minute to generate patient specific diet, Please continue your work
            </p>
          </div>
          <div className="popup-actions">
            <Button 
              onClick={() => {
                setShowGeneratePlanPopup(false)
                onCloseToIndex?.()
              }}
              className="popup-button"
            >
              Okay
            </Button>
          </div>
        </div>
      </Popup>
    </div>
  )
}
